import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { Tab, TabConfig, NavigationConfig } from '../../models';
import {
  ADMIN_ROUTES,
  HIDE_TABS_ROUTES,
  CALCULATOR_TITLES,
  ADMIN_TITLES
} from '../../constants/layout.constants';

/**
 * Service for managing header navigation logic
 * Follows Single Responsibility Principle by handling only navigation concerns
 */
@Injectable({
  providedIn: 'root'
})
export class HeaderNavigationService {
  private navigationConfigSubject = new BehaviorSubject<NavigationConfig>({
    showTabs: true,
    activeIndex: 0,
    isAdminMode: false
  });

  public navigationConfig$: Observable<NavigationConfig> = this.navigationConfigSubject.asObservable();

  constructor(private router: Router) { }

  /**
   * Determines if current route is an admin route
   */
  isAdminRoute(url: string): boolean {
    return ADMIN_ROUTES.some(route => url.includes(route));
  }

  /**
   * Determines if tabs should be shown for current route
   */
  shouldShowTabs(url: string): boolean {
    return !HIDE_TABS_ROUTES.some(route => url.includes(route));
  }

  /**
   * Updates navigation configuration based on current route
   */
  updateNavigationConfig(url: string, activeIndex: number = 0): void {
    const config: NavigationConfig = {
      showTabs: this.shouldShowTabs(url),
      activeIndex,
      isAdminMode: this.isAdminRoute(url)
    };
    this.navigationConfigSubject.next(config);
  }

  /**
   * Gets current navigation configuration
   */
  getCurrentConfig(): NavigationConfig {
    return this.navigationConfigSubject.value;
  }

  /**
   * Gets tabs based on current mode (admin vs regular)
   */
  getTabs(isAdminMode: boolean): Tab[] {
    if (isAdminMode) {
      return [
        { title: CALCULATOR_TITLES.PREMIX, content: 'admin-premix', key: 'admin-premix' },
        { title: CALCULATOR_TITLES.TECHNICAL, content: 'admin-technical', key: 'admin-technical' },
        { title: CALCULATOR_TITLES.RESISTANCE, content: 'admin-resistance-management', key: 'admin-resistance-management' }
      ];
    }

    return [
      { title: CALCULATOR_TITLES.PREMIX, content: 'premix', key: 'premix' },
      { title: CALCULATOR_TITLES.JARMIX, content: 'jarmix', key: 'jarmix' },
      { title: CALCULATOR_TITLES.ATS, content: 'ats', key: 'ats' },
      { title: CALCULATOR_TITLES.TECHNICAL, content: 'technical', key: 'technical' },
      { title: CALCULATOR_TITLES.PROGRAM_BUILDER, content: 'program-builder', key: 'program-builder' },
      { title: CALCULATOR_TITLES.RESISTANCE, content: 'resistance-management', key: 'resistance-management' }
    ];
  }
}
